/**
 * `selectKpis` — the composition the Dashboard, Analytics and the Coach's Report
 * all read from.
 *
 * It does no arithmetic. It resolves four UI-state terms (which accounts are
 * selected, which of them are excluded from metrics, the display unit, the
 * contract catalogue) into the arguments `domain/computeKpis` requires, and hands
 * back what that returns.
 *
 * The signature is the point: every input is a parameter. Nothing here reads a
 * store, so the identical call runs in a Deno Edge Function with the rows fetched
 * server-side — which is what Phase 3 is for.
 */
import { computeKpis, type KpiTrade, type Kpis } from '@domain/kpis';
import { countsForMetrics, type ContractSpec } from '@domain/trade';
import type { PnlUnit } from '@domain/types';
import {
  metricsContextFactory,
  tradesForSelection,
  type AccountFilterAccount,
  type AccountSelection,
} from './accounts';

/** What a KPI view needs from a trade: the money, the clock, and its account. */
export interface KpiViewTrade extends KpiTrade {
  accountId: string | null;
}

export interface KpiView<T extends KpiViewTrade = KpiViewTrade> {
  trades: readonly T[];
  accounts: readonly AccountFilterAccount[];
  contracts: readonly ContractSpec[];
  /** Empty means "all accounts" — not "no accounts". */
  selection: AccountSelection;
  unit: PnlUnit;
}

/**
 * KPIs for the current account selection.
 *
 * ⚠ The account selection is applied TWICE, and both are deliberate:
 *
 *   1. `tradesForSelection` narrows the population to what the user is looking at.
 *   2. `countsForMetrics` then drops trades on excluded accounts — unless the
 *      selection is excluded-only, in which case the exclusion lifts.
 *
 * They are not redundant. Step 1 is "what is in view"; step 2 is "what is allowed
 * to move a number". Under a mixed selection containing one SIM account, step 1
 * keeps its trades listed and step 2 keeps them out of the totals — which is the
 * entire purpose of the exclude flag.
 */
export function selectKpis<T extends KpiViewTrade>(view: KpiView<T>): Kpis<T> {
  const inView = tradesForSelection(view.trades, view.selection, view.accounts);
  const ctxFor = metricsContextFactory(view.accounts, view.selection);
  return computeKpis(inView, view.unit, view.contracts, (t) => countsForMetrics(t, ctxFor(t)));
}

// ── Types re-exported for ui/ ────────────────────────────────────────────────
//
// ⚠ Same mechanical rule as MetricsRow and effectiveTradeDate: `ui/` may not
// import `domain/` at all, and naming `Kpis` in a prop type still counts. A KPI
// strip that cannot name its own input would end up typing it `any` — which is
// how a component starts reading fields the aggregate does not promise.
export type { Kpis, KpiTrade } from '@domain/kpis';
export type { PnlUnit } from '@domain/types';
